import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createTypeThunk,
  loadTypes,
  updateTypeThunk,
} from '../features/documents/documentsSlice';
import type { DocumentType, DocumentTypePayload } from '../features/documents/types';
import { AppDispatch, RootState } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RequiredFieldsLegend } from '../components/RequiredFieldsLegend';

const schema = z.object({
  code: z.string().min(1, 'Requerido').max(32),
  name: z.string().min(1, 'Requerido').max(160),
  codePrefix: z.string().trim().max(16).optional(),
  description: z.string().trim().max(5000).optional(),
  retentionMonths: z.coerce.number().int().min(1).max(600).optional().or(z.literal('')),
  dispositionAction: z.enum(['conservar', 'archivar', 'eliminar']).optional().or(z.literal('')),
  active: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

function emptyToNull(v: string | undefined): string | null {
  return v && v.length > 0 ? v : null;
}

export function DocumentTypesPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { canEdit } = usePlatformAccessLevel('documents.access');
  const { types, typesLoading } = useSelector((s: RootState) => s.documents);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentType | null>(null);

  useEffect(() => {
    dispatch(loadTypes(false));
  }, [dispatch]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((t: DocumentType) => {
    setEditing(t);
    setDialogOpen(true);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Tipos de documento</h1>
        {canEdit && (
          <button
            onClick={openCreate}
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
          >
            + Nuevo tipo
          </button>
        )}
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Código</Th>
              <Th>Prefijo</Th>
              <Th>Nombre</Th>
              <Th>Retención</Th>
              <Th>Activo</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {typesLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!typesLoading && types.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                  Sin tipos definidos
                </td>
              </tr>
            )}
            {!typesLoading &&
              types.map((t) => (
                <tr key={t.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                  <td className="px-3 py-2 font-mono text-xs">{t.codePrefix ?? '—'}</td>
                  <td className="px-3 py-2">{t.name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {t.retentionMonths
                      ? `${t.retentionMonths} meses · ${t.dispositionAction ?? '—'}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">{t.active ? 'Sí' : 'No'}</td>
                  <td className="px-3 py-2">
                    {canEdit && (
                      <button
                        onClick={() => openEdit(t)}
                        className="text-xs rounded bg-secondary px-2 py-1 hover:bg-secondary/80"
                      >
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar tipo' : 'Nuevo tipo de documento'}</DialogTitle>
          </DialogHeader>
          <TypeForm
            initial={editing}
            readOnly={!canEdit}
            onSubmit={async (payload) => {
              if (editing) {
                await dispatch(updateTypeThunk({ id: editing.id, payload })).unwrap();
              } else {
                await dispatch(createTypeThunk(payload)).unwrap();
              }
              setDialogOpen(false);
            }}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TypeForm({
  initial,
  onSubmit,
  onCancel,
  readOnly,
}: {
  initial: DocumentType | null;
  onSubmit: (payload: DocumentTypePayload) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: initial?.code ?? '',
      name: initial?.name ?? '',
      codePrefix: initial?.codePrefix ?? '',
      description: initial?.description ?? '',
      retentionMonths: initial?.retentionMonths ?? '',
      dispositionAction: initial?.dispositionAction ?? '',
      active: initial?.active ?? true,
    },
  });

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      code: values.code,
      name: values.name,
      codePrefix: emptyToNull(values.codePrefix),
      description: emptyToNull(values.description),
      retentionMonths:
        values.retentionMonths === '' || values.retentionMonths === undefined
          ? null
          : Number(values.retentionMonths),
      dispositionAction:
        values.dispositionAction === '' || !values.dispositionAction
          ? null
          : values.dispositionAction,
      active: values.active,
    });
  });

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Código *" error={errors.code?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: PRO"
          {...register('code')}
        />
      </Field>
      <Field label="Prefijo de código" error={errors.codePrefix?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: PRO-"
          {...register('codePrefix')}
        />
      </Field>
      <Field label="Nombre *" error={errors.name?.message} colSpan={2}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: Procedimiento"
          {...register('name')}
        />
      </Field>
      <Field label="Descripción" error={errors.description?.message} colSpan={2}>
        <textarea
          rows={3}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Descripción del tipo de documento"
          {...register('description')}
        />
      </Field>
      <Field label="Retención (meses)" error={errors.retentionMonths?.message}>
        <input
          type="number"
          min={1}
          max={600}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: 60"
          {...register('retentionMonths')}
        />
      </Field>
      <Field label="Disposición al vencer" error={errors.dispositionAction?.message}>
        <select
          className="border rounded-md px-3 py-2 w-full"
          disabled={readOnly}
          {...register('dispositionAction')}
        >
          <option value="">Sin definir</option>
          <option value="conservar">Conservar</option>
          <option value="archivar">Archivar</option>
          <option value="eliminar">Eliminar</option>
        </select>
      </Field>
      <Field label="Activo" colSpan={2}>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" disabled={readOnly} {...register('active')} />
          <span className="text-sm text-muted-foreground">Disponible al crear nuevos documentos</span>
        </label>
      </Field>
      <RequiredFieldsLegend className="md:col-span-2" />
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
        >
          Cancelar
        </button>
        {!readOnly && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
          </button>
        )}
      </div>
    </form>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Field({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
